import {useState} from "react";
import {Alert} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import MainActions from "@/components/MainActions";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import MatchReportsPage from "@/pages/MatchReports";

const labels = {
	minutes: "Minuti",
	goals: "Gol fatti",
	assists: "Assist",
	yellowCards: "Gialli",
	redCard: "Espulsioni",
	goalsConceded: "Gol subiti",
};
const additional = {
	rating: "Voto",
	keyPasses: "Passaggi chiave",
	duelsWon: "Duelli vinti",
	duelsTotal: "Duelli totali",
	interceptions: "Intercetti",
	recoveries: "Recuperi",
	totalShots: "Tiri totali",
	shotsOnTarget: "Tiri in porta",
	dribblesAttempted: "Dribbling tentati",
	dribblesSuccessful: "Dribbling riusciti",
	crossesAttempted: "Cross tentati",
	crossesCompleted: "Cross riusciti",
	saves: "Parate",
	outOfAreaReadings: "Letture fuori area",
	lowReadings: "Letture basse",
	highReadings: "Letture alte",
	shortHorizontalPassRight: "Passaggi corti dx",
	shortHorizontalPassLeft: "Passaggi corti sx",
	shortVerticalPass: "Passaggi verticali",
	killerPass: "Killer pass",
	longFrontalPass: "Passaggi lunghi frontali",
	longPassRight: "Passaggi lunghi dx",
	longPassLeft: "Passaggi lunghi sx",
	strengths: "Punti di forza",
	improvements: "Da migliorare",
};
const sourceLabel = {
	events: "dalla cronaca",
	manual: "inserito a mano",
	conflict: "da verificare",
	uncollected: "non inserito",
};

const SOURCE_OPTIONS = [
	{value: "events", label: "Dalla cronaca"},
	{value: "manual", label: "Inserito a mano"},
];

export default function PlayerStatsTab({
	matchData,
	matchId,
	userRole,
	onEditStat,
	onDeleteStat,
	onPlayerClick,
	onReport,
	onEmptyAction,
	onSaveSources,
}) {
	const [comparison, setComparison] = useState(null);
	const [sources, setSources] = useState({});
	const [saving, setSaving] = useState(false);
	const statistics = matchData?.playerStatistics || [];
	const canEdit = !["Player", "Analyst"].includes(userRole);
	const differences = (row) =>
		Object.entries(row?.metrics || {}).filter(
			([, metric]) =>
				metric.manualValue != null &&
				metric.eventValue != null &&
				metric.manualValue !== metric.eventValue,
		);
	const compare = (row) => {
		setComparison(row);
		setSources(
			Object.fromEntries(
				differences(row).map(([key, metric]) => [
					key,
					metric.conflict ? "" : metric.source,
				]),
			),
		);
	};
	return (
		<div className="row no-margin g-1">
			<div className="col-12 padding-small">
				<p>
					Statistiche dei giocatori del tuo club. Goal, assist, cartellini e
					minuti seguono la completezza dichiarata degli eventi.
				</p>
				{!statistics.length && (
					<div className="match-empty">
						<p>
							Nessuna statistica: si ricavano dalla distinta e dagli eventi
							della gara.
						</p>
						{onEmptyAction && (
							<MainActions
								actions={[
									{
										icon: <FormatListNumberedIcon />,
										label: "Vai alla distinta",
										onClick: onEmptyAction,
									},
								]}
							/>
						)}
					</div>
				)}
				<div className="ad-player-list">
					{statistics.map((row) => {
						const minutes = row.metrics.minutes?.value;
						const conflict = Object.values(row.metrics).some(
							(metric) => metric.conflict,
						);
						return (
							<div key={row.participationId} className="ad-player-row">
								<div className="ad-player-row__main">
									<div
										className="ad-player-identity clickable"
										onClick={() => onPlayerClick(row.playerId)}
									>
										<span className="ad-player-name">
											#{row.playerNumber} {row.playerName}
										</span>
										<span className="ad-player-role">
											{row.goalkeeper ? "Portiere" : row.role} ·{" "}
											{row.matchSide === "home" ? "Casa" : "Trasferta"}
										</span>
									</div>
									<div className="ad-minutes-block">
										<div className="ad-minutes-bar-wrap">
											<div
												className="ad-minutes-bar-fill"
												style={{
													width:
														Math.min(
															100,
															((minutes || 0) /
																(matchData.sportingMinutes || 90)) *
																100,
														) + "%",
												}}
											/>
										</div>
										<span className="ad-minutes-label">
											{minutes == null ? "—" : minutes + "′"}
											<small>
												{" "}
												· {sourceLabel[row.metrics.minutes?.source]}
											</small>
										</span>
									</div>
									<div className="ad-row-actions">
										<MainActions
											actions={[
												row.goalkeeper && {
													icon: <DescriptionIcon />,
													title: "Report portiere",
													onClick: () => onReport(row.playerId),
												},
												differences(row).length > 0 && {
													icon: <CompareArrowsIcon />,
													title: "Confronta con la cronaca",
													color: conflict ? "warning" : undefined,
													disabled: !canEdit,
													onClick: () => compare(row),
												},
												{
													icon: <EditIcon />,
													title: "Completa statistiche",
													disabled:
														!canEdit ||
														row.warning?.startsWith("Valutazioni manuali"),
													onClick: () => onEditStat(row),
												},
												row.evaluationId && {
													icon: <CloseIcon />,
													title: "Elimina i dati inseriti a mano",
													color: "error",
													disabled: !canEdit,
													onClick: () =>
														onDeleteStat({
															id: row.evaluationId,
															participationId: row.participationId,
														}),
												},
											].filter(Boolean)}
										/>
									</div>
								</div>
								{conflict && (
									<Alert severity="warning">
										I valori inseriti a mano non coincidono con la cronaca:
										scegli quali tenere. Quelli inseriti a mano restano salvati.
									</Alert>
								)}
								{row.warning && <Alert severity="warning">{row.warning}</Alert>}
								<div className="ad-pro-stats">
									<div className="ad-pro-group">
										<div className="ad-pro-group-items">
											{Object.entries(row.metrics)
												.filter(([key]) => key !== "minutes")
												.map(([key, metric]) => (
													<div
														key={key}
														className="ad-pro-stat"
														title={sourceLabel[metric.source]}
													>
														<span className="ad-pro-stat-value">
															{metric.value ?? "—"}
														</span>
														<span className="ad-pro-stat-label">
															{labels[key]} · {sourceLabel[metric.source]}
														</span>
													</div>
												))}
										</div>
									</div>
									{Object.entries(additional)
										.filter(
											([key]) =>
												row.values[key] != null && row.values[key] !== "",
										)
										.map(([key, label]) => (
											<div key={key} className="ad-pro-group">
												<span className="ad-pro-stat-value">
													{row.values[key]}
												</span>
												<span className="ad-pro-stat-label">{label}</span>
											</div>
										))}
								</div>
							</div>
						);
					})}
				</div>
			</div>
			{userRole !== "Player" && (
				<div className="col-12 padding-small mt-2">
					<div className="ad-section-header">
						<span className="ad-section-title">Report portieri</span>
					</div>
					<MatchReportsPage matchId={matchId} />
				</div>
			)}
			<VLFModal
				open={!!comparison}
				maxWidth="md"
				title={"Confronta con la cronaca · " + (comparison?.playerName || "")}
				close={() => !saving && setComparison(null)}
				disabled={saving || Object.values(sources).some((source) => !source)}
				confirmLabel="Conferma"
				confirm={async () => {
					setSaving(true);
					try {
						const expected = Object.fromEntries(
							differences(comparison).map(([key, metric]) => [
								key,
								{
									manualValue: metric.manualValue,
									eventValue: metric.eventValue,
								},
							]),
						);
						if (await onSaveSources(comparison, sources, expected))
							setComparison(null);
					} finally {
						setSaving(false);
					}
				}}
				content={
					<>
						<p>
							Scegli il valore da tenere. Quelli inseriti a mano restano
							salvati; scegliendo la cronaca, le statistiche si aggiornano con i
							nuovi eventi.
						</p>
						<div className="table-responsive">
							<table className="table">
								<thead>
									<tr>
										<th>Statistica</th>
										<th>Inserito a mano</th>
										<th>Dalla cronaca</th>
										<th>Da tenere</th>
									</tr>
								</thead>
								<tbody>
									{differences(comparison).map(([key, metric]) => (
										<tr key={key}>
											<td>{labels[key]}</td>
											<td>{metric.manualValue}</td>
											<td>{metric.eventValue}</td>
											<td>
												<VLFSelectNew
													tableField
													aria-label={"Valore da tenere " + labels[key]}
													options={SOURCE_OPTIONS}
													value={
														SOURCE_OPTIONS.find(
															(o) => o.value === sources[key],
														) || null
													}
													onChange={(option) =>
														setSources({...sources, [key]: option?.value || ""})
													}
													sorted={false}
													isClearable={false}
												/>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				}
			/>
		</div>
	);
}
